import { Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "../hooks/useAuth";
import {
  updatePasswordSchema,
  type UpdatePasswordFormValues,
} from "../schema/auth.schema";
import SEO from "../components/SEO/SEO";

const getPasswordValidationState = (pwd: string) => {
  const minLength = pwd.length >= 8;
  const hasUpperCase = /[A-Z]/.test(pwd);
  const hasLowerCase = /[a-z]/.test(pwd);
  const hasNumbers = /\d/.test(pwd);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

  return {
    minLength,
    hasUpperCase,
    hasLowerCase,
    hasNumbers,
    hasSpecialChar,
    isValid:
      minLength &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumbers &&
      hasSpecialChar,
  };
};

const UpdatePassword: React.FC = () => {
  const { updateUserPassword } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  const form = useForm<UpdatePasswordFormValues>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  const password = form.watch("password");
  const confirmPassword = form.watch("confirmPassword");
  const passwordValidation = getPasswordValidationState(password || "");
  const passwordsMatch =
    password === confirmPassword && Boolean(confirmPassword && confirmPassword.length);

  const handleSubmit = async (values: UpdatePasswordFormValues) => {
    setLoading(true);
    setMessage("");
    setSubmitError("");

    try {
      const { error } = await updateUserPassword(values.password);
      if (error) {
        setSubmitError(error.message);
      } else {
        setMessage("Password updated successfully! Redirecting...");
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }
    } catch {
      setSubmitError("Failed to update password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <SEO title="Update Password" description="Set a new password for your My Perfume account." noindex />
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg border border-neutral-200 overflow-hidden">
          <div className="bg-white p-8 text-center border-b border-neutral-200">
            <img
              src="/assets/Images/logo/logo.webp"
              alt="My Perfume"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h2 className="text-2xl font-bold text-neutral-800 mb-2">
              Update Your Password
            </h2>
            <p className="text-neutral-600 text-sm">
              Enter your new password below.
            </p>
          </div>

          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 p-8">
            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  {...form.register("password")}
                  autoComplete="new-password"
                  className="w-full pl-12 pr-12 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400"
                  placeholder="Enter new password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {password && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs text-neutral-600 mb-2">
                    Password must contain:
                  </div>
                  <div className="space-y-1">
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.minLength ? "text-green-600" : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.minLength ? "text-green-600" : "text-neutral-300"
                        }`}
                      />
                      At least 8 characters
                    </div>
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.hasUpperCase
                          ? "text-green-600"
                          : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.hasUpperCase
                            ? "text-green-600"
                            : "text-neutral-300"
                        }`}
                      />
                      One uppercase letter
                    </div>
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.hasLowerCase
                          ? "text-green-600"
                          : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.hasLowerCase
                            ? "text-green-600"
                            : "text-neutral-300"
                        }`}
                      />
                      One lowercase letter
                    </div>
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.hasNumbers ? "text-green-600" : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.hasNumbers ? "text-green-600" : "text-neutral-300"
                        }`}
                      />
                      One number
                    </div>
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.hasSpecialChar
                          ? "text-green-600"
                          : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.hasSpecialChar
                            ? "text-green-600"
                            : "text-neutral-300"
                        }`}
                      />
                      One special character
                    </div>
                  </div>
                </div>
              )}

              {form.formState.errors.password && (
                <p className="mt-2 text-xs text-red-600">
                  {form.formState.errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  {...form.register("confirmPassword")}
                  autoComplete="new-password"
                  className="w-full pl-12 pr-12 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400"
                  placeholder="Confirm new password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {confirmPassword && (
                <div
                  className={`mt-2 text-xs ${
                    passwordsMatch ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                </div>
              )}

              {form.formState.errors.confirmPassword && (
                <p className="mt-2 text-xs text-red-600">
                  {form.formState.errors.confirmPassword.message}
                </p>
              )}
            </div>

            {message && (
              <div className="text-green-600 text-sm bg-green-50 p-3 rounded-lg border border-green-200">
                {message}
              </div>
            )}
            {submitError && (
              <div className="text-red-700 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !passwordValidation.isValid || !passwordsMatch}
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-light focus:ring-4 focus:ring-primary/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default UpdatePassword;
